<template>
  <slot name="header">
    <div
      class="tw-px-[20px] tw-pt-[20px] tw-pb-[10px] tw-flex tw-items-center tw-justify-between tw-h-[46px]"
    >
      <div class="tw-text-base tw-font-semibold tw-w-full tw-text-[#211F58]">
        {{ title }}
      </div>
      <ButtonShowDuplicateAssessment
        :id-popover="'popover-cyber-health-score'"
        :class="`tw-p-[15px]`"
      >
        <div class="tw-p-[15px] tw-w-[247px]">
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="tw-flex tw-items-center">
              <CalendarIcon />
            </div>
            <div class="title-note">
              {{
                noteTitle ??
                getComponentUiText(
                  "app-assess-recommendations-cyber-health-score-note-title-1",
                )
              }}
            </div>
          </div>
          <div
            class="tw-mt-[10px] tw-text-[11px] tw-font-[400] tw-text-[#878787]"
          >
            {{
              noteDescription ??
              getComponentUiText(
                "app-assess-recommendations-cyber-health-score-description-1",
              )
            }}
          </div>
        </div>
      </ButtonShowDuplicateAssessment>
    </div>
  </slot>
  <div v-if="isLoading" class="tw-flex tw-w-full tw-h-[134px]">
    <span class="custom-global-skeleton tw-w-full tw-h-full"></span>
  </div>
  <div
    v-if="!isLoading"
    class="tw-px-[20px] tw-py-4 tw-flex tw-items-center tw-justify-between tw-h-[45px]"
  >
    <div class="tw-text-[12px] tw-font-medium">{{ data[0].title }}</div>
    <div class="tw-text-[13px] tw-font-semibold tw-text-[#878787]">
      {{
        type === "scoreIncrease"
          ? Math.round(+score.currentSCore)
          : Number(score.maxScore)
      }}
    </div>
  </div>
  <div
    v-if="!isLoading"
    class="tw-px-[20px] tw-py-4 tw-flex tw-items-center tw-justify-between tw-border-t-[1px] tw-border-0 tw-border-solid tw-border-[#EFF0F1] tw-h-[45px]"
  >
    <div class="tw-text-[12px] tw-font-medium">
      {{ data[1].title }}
    </div>
    <div class="tw-text-[13px] tw-font-semibold tw-text-[#878787]">
      {{
        type === "scoreIncrease"
          ? Math.round(+score.maxScore)
          : Number(score.maxScore)
      }}
    </div>
  </div>
  <slot v-if="!isLoading" name="footer">
    <div
      class="tw-px-[20px] tw-py-4 tw-flex tw-items-center tw-justify-between tw-border-t-[1px] tw-border-0 tw-border-solid tw-border-[#EFF0F1] tw-h-[45px]"
    >
      <div class="tw-text-[12px] tw-font-medium">
        {{
          getComponentUiText(
            "app-assess-recommendations-cyber-health-score-sub-title-3",
          )
        }}
      </div>
      <div
        v-show="Number(scoreIncrease) >= 0"
        class="tw-text-[16px] tw-font-semibold tw-text-[#01893D]"
      >
        <span v-show="Number(scoreIncrease) === 0">{{ scoreIncrease }}</span>
        <span v-show="Number(scoreIncrease) > 0"
          ><span class="tw-text-[14px] tw-mr-[3px] tw-font-medium">+</span
          >{{ scoreIncrease }}</span
        >
      </div>
      <div
        v-show="Number(scoreIncrease) < 0"
        class="tw-text-[16px] tw-font-semibold tw-text-[#F7553C]"
      >
        <span v-show="Number(scoreIncrease) < 0"
          ><span class="tw-text-[14px] tw-mr-[3px] tw-font-medium">-</span
          >{{ scoreIncrease.toString().replace("-", "") }}</span
        >
      </div>
    </div>
  </slot>
</template>

<script setup lang="ts">
import { PropType, computed } from "vue";
import ButtonShowDuplicateAssessment from "../common/setting/buttonShowDuplicateAssessment.vue";
import { getComponentUiText } from "@/utils/utilities";
import CalendarIcon from "@/components/svgs/calendarIcon.vue";

export type dataScore = {
  title: string;
  id: string;
  value: number;
};

const props = defineProps({
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
  score: {
    type: Object,
    required: true,
    default: () => ({
      currentSCore: 425,
      maxScore: 40,
    }),
  },
  data: {
    type: Array as () => dataScore[],
    required: true,
    default: () => [
      {
        title: getComponentUiText(
          "app-assess-recommendations-cyber-health-score-sub-title-1",
        ),
        id: "currentScore",
        value: 425,
      },
      {
        title: getComponentUiText(
          "app-assess-recommendations-cyber-health-score-sub-title-2",
        ),
        id: "potentialScore",
        value: 40,
      },
    ],
  },
  title: {
    type: String,
    default: "",
    required: false,
  },
  noteTitle: {
    type: String,
    default: null,
    required: false,
  },
  noteDescription: {
    type: String,
    default: null,
    required: false,
  },
  type: {
    type: String as PropType<"cyberHealthScore" | "scoreIncrease">,
    required: false,
    default: "cyberHealthScore",
  },
});

const scoreIncrease = computed(() => {
  return props.type == "scoreIncrease"
    ? Math.round(+props.score.maxScore) - Math.round(+props.score.currentSCore)
    : (props.score.maxScore - props.score.currentSCore).toString();
});
</script>

<style scoped>
.title-note {
  color: var(--Brand-Navy, #211f58);
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
}
</style>
